/* eslint-disable react/display-name */
import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { ListItem, Button, Collapse, colors, Checkbox, Typography, Box } from '@material-ui/core';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import HelpIcon from '@material-ui/icons/Help';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import Tooltip from '@material-ui/core/Tooltip';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%'
  },
  buttonLeaf: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'space-between',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '& .MuiButton-label > div': {
      display: 'flex'
    }
  },
  icon: {
    color: theme.palette.icon,
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 16,
    width: 16
  },
  totalItems: {
    marginLeft: theme.spacing(1)
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  active: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    '& $icon': {
      color: theme.palette.primary.main
    }
  },
  checkbox: {
    padding: 0
  },
  tooltipContent: {
    color: 'white',
    fontSize: '14px',
    lineHeight: '1.5',
    fontWeight: '400'
  }
}));

const NavigationListItem = props => {
  const {
    count,
    checked,
    page,
    handleSelect,
    depth,
    children,
    className,
    open: openProp,
    label: Label,
    ...rest
  } = props;

  const { id, name, title, description, colCount, rowCount } = page;

  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen(open => !open);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = (32 + 8) * depth;
  }

  const style = {
    paddingLeft
  };

  if (children) {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.item, className)}
        disableGutters
      >
        <div className={classes.itemLeaf}>
          <Button
            className={classes.button}
            onClick={handleToggle}
            style={style}
          >
            {open ?
              <FolderOpenIcon className={classes.icon} /> :
              <FolderIcon className={classes.icon} />
            }
            {name}
            {count > 0 && (
              <Typography
                className={classes.totalItems}
                variant="body2"
              >
                ({count > 99 ? '99+' : count})
              </Typography>
            )}
          </Button>
        </div>
        <Collapse in={open}>{children}</Collapse>
      </ListItem >
    );
  } else {
    return (
      <Fragment>
        <ListItem
          {...rest}
          className={clsx(classes.itemLeaf, className)}
          disableGutters
        >
          <Button
            className={clsx(classes.buttonLeaf, `depth-${depth}`)}
            onClick={() => handleSelect(page)}
            style={style}
          >
            <Box alignItems={'center'} display={'flex'}>
              <InsertDriveFileIcon className={classes.icon} />
              {name}
              {Label && (
                <span className={classes.label}>
                  <Label />
                </span>
              )}
              <Tooltip
                title={<div className={classes.tooltipContent}>
                  <p>Title: {title}</p>
                  <p>Description: {description}</p>
                  <p>Rows: {rowCount}</p>
                  <p>Columns: {colCount}</p>
                </div>}
              >
                <HelpIcon fontSize='small' />
              </Tooltip>
            </Box>
            <Checkbox
              checked={checked.findIndex(el => el.id === id) !== -1}
              className={classes.checkbox}
              disableRipple
              tabIndex={-1}
            />
          </Button>
        </ListItem >
      </Fragment>
    );
  }
};

NavigationListItem.propTypes = {
  checked: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  className: PropTypes.string,
  count: PropTypes.number,
  depth: PropTypes.number.isRequired,
  handleSelect: PropTypes.func,
  label: PropTypes.node,
  open: PropTypes.bool,
  page: PropTypes.object.isRequired,
};

NavigationListItem.defaultProps = {
  depth: 0,
  open: false,
  page: {},
};

export default NavigationListItem;
